import React, { useState } from 'react';
import {
  Stack,
  Typography,
  Tabs,
  Tab,
  TextField,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';

import 'handsontable/dist/handsontable.full.min.css';
import { registerAllModules } from 'handsontable/registry';
import { useLocalization } from 'src/context/LocalizationContext';
import DhlReportList from './Views/FastReportListPages/DHLReportList';
import ArasReportList from './Views/FastReportListPages/ArasReportList';
import TaxFreeReportList from './Views/FastReportListPages/TaxFreeReportList';
import EticaretReportList from './Views/FastReportListPages/EticaretReportList';
import * as XLSX from 'xlsx';
import moment from 'moment';
import ExportToExcelButton from 'src/components/shared/ExportToExcelButton';
import { toast } from 'react-toastify';
import FilterButton from 'src/components/shared/FilterButton';
import ClearButton from 'src/components/shared/ClearButton';

registerAllModules();

export default function FastReportList() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const { language } = useLocalization();
  const [selectedTab, setSelectedTab] = useState("E-TİCARET");
  const [filteredReportsByTab, setFilteredReportsByTab] = useState({
    "E-TİCARET": [],
    DHL: [],
    "TAX FREE": [],
    ARAS: [],
  });
  const [pendingDateRange, setPendingDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [appliedDateRange, setAppliedDateRange] = useState({
    startDate: '',
    endDate: '',
  });
  const [filterVersion, setFilterVersion] = useState(0);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };
  const fDate = (ts) => {
    if (!ts) return "-";
    const d = new Date(ts * 1000);
    const y = d.getFullYear();
    if (y === 1970) return "-";
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    return `${dd}.${mm}.${y}`;
  };

  const mapDHL = (list = []) => list.map((x, i) => ({
    "No": i + 1,
    "İşlem Tarihi": fDate(x.operationDate),
    "Konşimento Numarası (Waybill)": x.waybill,
    "Gönderen Adı": x.senderName,
    "Alıcı Adı": x.receiverName,
    "İşlem Yapan EP Çalışanı": x.processingStaff,
    "Teslim Eden EP Çalışanı": x.deliveredStaff || "-",
    "Teslim Tarihi": fDate(x.deliveredDate),
    "Tutar (TL)": x.price || "-",
    "Lokasyon": x.poiTitle,
    "Açıklama": x.description,
    "Not": x.additionalNote || "-"
  }));

  const mapARAS = (list = []) => list.map((x, i) => ({
    "No": i + 1,
    "İşlem Tarihi": fDate(x.inBranchDate),
    "Gönderi No": x.barcode,
    "Müşteri Ad Soyad": x.customerName,
    "EP Personel Ad Soyadı": x.staff,
    "Açıklama": x.description || "-",
    "İşlem Türü": x.processingType,
    "Tutar": x.price || "-",
    "Ödeme Türü": x.paymentType,
    "İşlem Detay": x.processingDetails,
    "Lokasyon": x.poiTitle,
  }));

  const mapETICARET = (list = []) => list.map((x, i) => ({
    "No": i + 1,
    "E-Ticaret Markası": x.eCommerceBrand || "-",
    "Kargo Geliş Tarihi": x.inBranchDate ? fDate(x.inBranchDate) : "-",
    "Barkod Numarası": x.barcode || "-",
    "Pre-Printed Barkod": x.preprintedBarcode || "-",
    "İade Kodu": x.returnBarcode || "-",
    "Alıcı Müşteri Adı": x.receiverName || "-",
    "Teslim Alan Müşteri Adı": x.takenByName || "-",
    "Teslim Eden EP Çalışanı": x.deliveredStaff || "-",
    "Lokasyon": x.poiTitle || "-",
    "Müşteriye Teslim Tarihi": x.deliveredDate ? fDate(x.deliveredDate) : "-",
    "İşlem Türü": x.processingType || "-",
    "İade Tarihi": x.returnDate ? fDate(x.returnDate) : "-"
  }));

  const mapTAXFREE = (list = []) => list.map((x, i) => ({
    "No": i + 1,
    "Marka": x.brand,
    "İşlem Tarihi": fDate(x.operationDate),
    "Müşteri Fatura No": x.customerInvoiceNo,
    "Müşteri İsmi": x.customerName,
    "Ülke": x.country,
    "Fatura Tutarı": x.price,
    "Ödenen İade Tutarı": x.returnPrice,
    "Ödeme Türü": x.paymentType,
    "Mağaza": x.store,
    "Lokasyon": x.poiTitle,
    "EP Çalışanı": x.staff,
    "Mağaza Lokasyonu": x.storeLocation,
    "Not": x.additionalNote || "-"
  }));

  const setFilteredDataForTab = (tabName, data) => {
    setFilteredReportsByTab((prev) => ({
      ...prev,
      [tabName]: Array.isArray(data) ? data : [],
    }));
  };

  const exportCurrentToExcel = async () => {
    const wb = XLSX.utils.book_new();

    const addSheet = (title, rows) => {
      const headers = rows.length ? Object.keys(rows[0]) : [];
      const aoa = [headers, ...rows.map(r => headers.map(h => r[h]))];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, ws, title);
    };

    const eticaretRows = filteredReportsByTab["E-TİCARET"] || [];
    const dhlRows = filteredReportsByTab.DHL || [];
    const taxFreeRows = filteredReportsByTab["TAX FREE"] || [];
    const arasRows = filteredReportsByTab.ARAS || [];
    const totalRowCount =
      eticaretRows.length + dhlRows.length + taxFreeRows.length + arasRows.length;

    if (!totalRowCount) {
      toast.info("Excel'e aktarılacak filtrelenmiş veri bulunamadı.");
      return;
    }

    addSheet("E-Ticaret Rapor", mapETICARET(eticaretRows));
    addSheet("DHL Rapor", mapDHL(dhlRows));
    addSheet("Tax Free Rapor", mapTAXFREE(taxFreeRows));
    addSheet("Aras Rapor", mapARAS(arasRows));

    const fileName = `SahaRaporuListesi_${moment().format("YYYYMMDD_HHmm")}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const handleApplyDateFilter = () => {
    if (!pendingDateRange.startDate || !pendingDateRange.endDate) {
      toast.info('Lütfen başlangıç ve bitiş tarihlerini seçin.');
      return;
    }
    if (pendingDateRange.startDate > pendingDateRange.endDate) {
      toast.error('Başlangıç tarihi bitiş tarihinden sonra olamaz.');
      return;
    }
    setFilteredReportsByTab({ "E-TİCARET": [], DHL: [], "TAX FREE": [], ARAS: [] });
    setAppliedDateRange({ ...pendingDateRange });
    setFilterVersion((version) => version + 1);
  };

  const handleClearDateFilter = () => {
    const cleared = { startDate: '', endDate: '' };
    setPendingDateRange(cleared);
    setAppliedDateRange(cleared);
    setFilteredReportsByTab({ "E-TİCARET": [], DHL: [], "TAX FREE": [], ARAS: [] });
  };

  const tabsComponent = (
    <Tabs
      value={selectedTab}
      onChange={handleTabChange}
      aria-label="Kargo Seçimi"
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      <Tab label="E-TİCARET" value="E-TİCARET" />
      <Tab label="DHL" value="DHL" />
      <Tab label="TAX FREE" value="TAX FREE" />
      <Tab label="ARAS" value="ARAS" />
    </Tabs>
  );

  return (
    <>
      <Helmet>
        <title>{language.fast_report_list.titles.site_title}</title>
      </Helmet>
      <Stack style={{ paddingInline: '25px' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            {language.fast_report_list.titles.title}
          </Typography>
          <div style={{display:'flex',flexDirection:'row',}}>
            <ExportToExcelButton onClick={exportCurrentToExcel} label="Excel'e Aktar" />
          </div>
        </Stack>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'flex-end' }}
          justifyContent={{ xs: 'stretch', md: 'flex-end' }}
          mb={3}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
            justifyContent="flex-end"
            width="100%"
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Başlangıç Tarihi"
                type="date"
                size="small"
                value={pendingDateRange.startDate}
                onChange={(e) =>
                  setPendingDateRange((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                sx={{
                  minWidth: 190,
                  '& .MuiOutlinedInput-root': {
                    height: 40,
                  },
                  '& .MuiInputBase-input': {
                    boxSizing: 'border-box',
                  },
                }}
              />
              <TextField
                label="Bitiş Tarihi"
                type="date"
                size="small"
                value={pendingDateRange.endDate}
                onChange={(e) =>
                  setPendingDateRange((prev) => ({
                    ...prev,
                    endDate: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: pendingDateRange.startDate || undefined }}
                sx={{
                  minWidth: 190,
                  '& .MuiOutlinedInput-root': {
                    height: 40,
                  },
                  '& .MuiInputBase-input': {
                    boxSizing: 'border-box',
                  },
                }}
              />
            </Stack>
            <Stack direction="row" spacing={1}>
              <ClearButton onClick={handleClearDateFilter} />
              <FilterButton onClick={handleApplyDateFilter} />
            </Stack>
          </Stack>
        </Stack>

        {!appliedDateRange.startDate && tabsComponent}
        {!appliedDateRange.startDate && (
          <Typography sx={{ mt: 3 }}>Raporları görüntülemek için başlangıç ve bitiş tarihlerini seçip Filtrele düğmesine basın.</Typography>
        )}
        {appliedDateRange.startDate && <div style={{ display: selectedTab === 'DHL' ? 'block' : 'none' }}>
          <DhlReportList
            key={filterVersion}
            parsedUser={parsedUser}
            tabs={tabsComponent}
            externalDateRange={appliedDateRange}
            onFilteredDataChange={(data) => setFilteredDataForTab("DHL", data)}
          />
        </div>}
        {appliedDateRange.startDate && <div style={{ display: selectedTab === 'TAX FREE' ? 'block' : 'none' }}>
          <TaxFreeReportList
            key={filterVersion}
            parsedUser={parsedUser}
            tabs={tabsComponent}
            externalDateRange={appliedDateRange}
            onFilteredDataChange={(data) => setFilteredDataForTab("TAX FREE", data)}
          />
        </div>}
        {appliedDateRange.startDate && <div style={{ display: selectedTab === 'ARAS' ? 'block' : 'none' }}>
          <ArasReportList
            key={filterVersion}
            parsedUser={parsedUser}
            tabs={tabsComponent}
            externalDateRange={appliedDateRange}
            onFilteredDataChange={(data) => setFilteredDataForTab("ARAS", data)}
          />
        </div>}
        {appliedDateRange.startDate && <div style={{ display: selectedTab === 'E-TİCARET' ? 'block' : 'none' }}>
          <EticaretReportList
            key={filterVersion}
            parsedUser={parsedUser}
            tabs={tabsComponent}
            externalDateRange={appliedDateRange}
            onFilteredDataChange={(data) => setFilteredDataForTab("E-TİCARET", data)}
          />
        </div>}
      </Stack>
    </>
  );
}
